import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MoneyInput from "../../../serviceProvider/components/accounting/shared/MoneyInput";
import {
  formatCents,
  formatDate
} from "../../../serviceProvider/components/accounting/shared/money";
import * as accounting from "../../../serviceProvider/components/accounting/receivables/api";
import { getSettings } from "../../../serviceProvider/components/accounting/api";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { paymentMethodLabels } from "../../../utils/invoiceStatusArray";
import { invoiceErrorText } from "./invoiceErrors";
import { Success } from "../../Messages/Messages";
import "./InvoiceDialogs.css";

const METHODS = ["bank", "cash", "card", "paypal", "cheque", "other"];
const KIND_LABELS = {
  payment: "Payment",
  refund: "Refund",
  offset: "Offset",
  reversal: "Reversal"
};
const iso = d => (d ? dayjs(d).format("YYYY-MM-DD") : undefined);

/**
 * UI-3 / PAY-1..3: records a payment outside bank matching — method, amount, date and the
 * payment differences (Skonto, Kleindifferenz, withheld Bauabzugsteuer). An amount above the
 * open amount settles the invoice and leaves a customer credit.
 */
/**
 * PAY-2: the customer's deduction as Rechnungskorrektur free lines. The gross difference is
 * split over the invoice's rates in proportion to their gross (the last rate takes the
 * rounding remainder) and each part is converted to net at its rate.
 */
export const deductionFreeLines = (invoice, grossDiff) => {
  const taxable = (invoice?.taxMode || "regular") === "regular";
  const groups = (invoice?.amounts?.byRate || [])
    .map(g => ({
      rate: Number(g.rate || 0),
      gross: Number(g.gross ?? Number(g.net || 0) + Number(g.tax || 0))
    }))
    .filter(g => g.gross > 0);
  if (!groups.length) groups.push({ rate: taxable ? 19 : 0, gross: 1 });
  const total = groups.reduce((s, g) => s + g.gross, 0);
  let rest = grossDiff;
  return groups.map((g, i) => {
    const part = i === groups.length - 1 ? rest : Math.round((grossDiff * g.gross) / total);
    rest -= part;
    const rate = taxable ? g.rate : 0;
    return {
      description: "Minderung / Abzug des Kunden",
      quantity: 1,
      unit: "",
      unitPrice: Math.round((part * 100) / (100 + rate)),
      taxRate: rate
    };
  });
};

export const RecordPaymentDialog = ({
  open,
  invoice,
  onClose,
  onDone,
  canCorrect = false,
  onCorrection
}) => {
  const { t: ta } = useTranslation("accounting");
  const openAmount = Math.max(0, Number(invoice?.amounts?.open || 0));
  const [v, setV] = useState({});
  const [limit, setLimit] = useState(500);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [payFirst, setPayFirst] = useState(true);

  useEffect(() => {
    if (!open) return;
    setPayFirst(true);
    setV({
      amount: openAmount,
      method: "bank",
      date: dayjs(),
      skonto: null,
      smallDifference: null,
      withheld: null,
      note: ""
    });
    setError("");
    getSettings()
      .then(res => {
        const s = res.data?.settings || res.data || {};
        if (s.smallDifferenceLimit !== undefined) setLimit(Number(s.smallDifferenceLimit));
      })
      .catch(() => {});
  }, [open]);

  const set = (key, value) => setV(prev => ({ ...prev, [key]: value }));
  const amount = Number(v.amount || 0);
  const skonto = Number(v.skonto || 0);
  const smallDifference = Number(v.smallDifference || 0);
  const withheld = Number(v.withheld || 0);
  const applied = amount + skonto + smallDifference + withheld;
  const gap = openAmount - amount - skonto - withheld;
  const skontoPercent = Number(invoice?.paymentTerms?.skontoPercent || 0);

  const applySkonto = () => {
    const s = Math.round((openAmount * skontoPercent) / 100);
    setV(prev => ({ ...prev, skonto: s, amount: Math.max(0, openAmount - s - withheld) }));
  };
  const applyWithheld = () => {
    // § 48 EStG: 15 % of the gross amount
    const w = Math.round((Number(invoice?.amounts?.gross || openAmount) * 15) / 100);
    const cap = Math.min(w, openAmount);
    setV(prev => ({ ...prev, withheld: cap, amount: Math.max(0, openAmount - cap - skonto) }));
  };

  const submit = async () => {
    if (applied <= 0) {
      setError(ta("Enter the amount received"));
      return;
    }
    if (smallDifference > limit) {
      setError(
        ta("The payment difference is above the limit. Create an invoice correction instead.")
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await accounting.recordPayment(invoice._id, {
        amount,
        method: v.method,
        date: iso(v.date),
        skonto,
        smallDifference,
        withheld,
        note: v.note || ""
      });
      if (res.data?.credit?.amount)
        Success(
          ta("Payment recorded. A customer credit of {{amount}} was created.", {
            amount: formatCents(res.data.credit.amount)
          })
        );
      else Success(ta("Payment recorded"));
      onDone?.(res.data);
    } catch (err) {
      setError(invoiceErrorText(ta, err));
    } finally {
      setBusy(false);
    }
  };

  // PAY-2: an underpayment above the Kleindifferenz limit (customer deducted for defects)
  const deduction = openAmount - applied;
  const offerCorrection =
    canCorrect && typeof onCorrection === "function" && deduction > limit && applied < openAmount;

  const correctDifference = async () => {
    setError("");
    if (payFirst && applied > 0) {
      setBusy(true);
      try {
        await accounting.recordPayment(invoice._id, {
          amount,
          method: v.method,
          date: iso(v.date),
          skonto,
          smallDifference,
          withheld,
          note: v.note || ""
        });
        Success(ta("Payment recorded"));
      } catch (err) {
        setError(invoiceErrorText(ta, err));
        setBusy(false);
        return;
      }
      setBusy(false);
    }
    onCorrection({
      paid: payFirst && applied > 0,
      freeLines: deductionFreeLines(invoice, deduction),
      reason: { code: "defect", text: "" }
    });
  };

  let result = null;
  if (applied > 0) {
    if (applied < openAmount)
      result = {
        type: "info",
        text: ta("{{amount}} stays open", { amount: formatCents(openAmount - applied) })
      };
    else if (applied === openAmount)
      result = { type: "success", text: ta("The invoice is settled") };
    else
      result = {
        type: "warning",
        text: ta("Overpayment: a customer credit of {{amount}} is created", {
          amount: formatCents(applied - openAmount)
        })
      };
  }

  return (
    <Modal
      rootClassName="invoice-dialog"
      title={ta("Record payment")}
      open={open}
      width={640}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {ta("Cancel")}
        </Button>,
        <Button key="ok" type="primary" loading={busy} onClick={submit}>
          {ta("Record payment")}
        </Button>
      ]}
    >
      <Form layout="vertical" className="invoice-dialog__body">
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Typography.Text type="secondary">
          {ta("Open amount")}: <strong>{formatCents(openAmount)}</strong>
        </Typography.Text>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item label={ta("Amount received")}>
              <MoneyInput min={0} value={v.amount} onChange={x => set("amount", x)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={ta("Method")}>
              <Select
                value={v.method}
                onChange={x => set("method", x)}
                options={METHODS.map(m => ({ value: m, label: ta(paymentMethodLabels[m]) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={ta("Date")}>
              <DatePicker
                style={{ width: "100%" }}
                format="DD.MM.YYYY"
                allowClear={false}
                value={v.date}
                onChange={d => set("date", d)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={ta("Skonto")}
              extra={
                skontoPercent ? (
                  <Button type="link" size="small" onClick={applySkonto} className="px-0">
                    {ta("Apply {{percent}} % Skonto", { percent: skontoPercent })}
                  </Button>
                ) : null
              }
            >
              <MoneyInput min={0} value={v.skonto} onChange={x => set("skonto", x)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={ta("Small difference (written off)")}
              extra={
                <Space size={4} wrap>
                  <span>{ta("Limit {{amount}}", { amount: formatCents(limit) })}</span>
                  {gap > 0 && gap <= limit ? (
                    <Button
                      type="link"
                      size="small"
                      className="px-0"
                      onClick={() => set("smallDifference", gap)}
                    >
                      {ta("Write off {{amount}}", { amount: formatCents(gap) })}
                    </Button>
                  ) : null}
                </Space>
              }
            >
              <MoneyInput
                min={0}
                value={v.smallDifference}
                status={smallDifference > limit ? "error" : undefined}
                onChange={x => set("smallDifference", x)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={ta("Construction withholding tax withheld")}
              extra={
                <Button type="link" size="small" className="px-0" onClick={applyWithheld}>
                  {ta("15 % of the gross amount")}
                </Button>
              }
            >
              <MoneyInput min={0} value={v.withheld} onChange={x => set("withheld", x)} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label={ta("Note")}>
              <Input value={v.note} maxLength={200} onChange={e => set("note", e.target.value)} />
            </Form.Item>
          </Col>
        </Row>
        {offerCorrection ? (
          <Alert
            type="info"
            showIcon
            message={ta(
              "If the customer deducted an amount for defects, create an invoice correction for the difference."
            )}
            description={
              <div className="invoice-dialog__deduction">
                <Checkbox checked={payFirst} onChange={e => setPayFirst(e.target.checked)}>
                  {ta("Record the payment first")}
                </Checkbox>
                <Button loading={busy} onClick={correctDifference}>
                  {ta("Create invoice correction for the difference ({{amount}})", {
                    amount: formatCents(deduction)
                  })}
                </Button>
              </div>
            }
          />
        ) : null}
        {result ? <Alert type={result.type} showIcon message={result.text} /> : null}
      </Form>
    </Modal>
  );
};

/** PAY-4: Rücklastschrift / returned payment with an optional bank fee charged on. */
export const ReversePaymentDialog = ({ open, payment, onClose, onDone }) => {
  const { t: ta } = useTranslation("accounting");
  const [date, setDate] = useState(dayjs());
  const [reason, setReason] = useState("");
  const [returnFee, setReturnFee] = useState(null);
  const [chargeFee, setChargeFee] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDate(dayjs());
    setReason("");
    setReturnFee(null);
    setChargeFee(false);
    setError("");
  }, [open]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await accounting.reversePayment(payment._id, {
        date: iso(date),
        reason,
        returnFee: Number(returnFee || 0),
        chargeFee
      });
      Success(ta("Payment reversed; the invoice is open again"));
      onDone?.();
    } catch (err) {
      setError(invoiceErrorText(ta, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="invoice-dialog"
      title={ta("Reverse payment (returned direct debit)")}
      open={open}
      width={560}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {ta("Cancel")}
        </Button>,
        <Button key="ok" type="primary" danger loading={busy} onClick={submit}>
          {ta("Reverse payment")}
        </Button>
      ]}
    >
      <Form layout="vertical" className="invoice-dialog__body">
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Typography.Paragraph type="secondary">
          {ta(
            "The payment of {{amount}} from {{date}} is reversed and the invoice is open again.",
            { amount: formatCents(payment?.amount), date: formatDate(payment?.date) }
          )}
        </Typography.Paragraph>
        <Form.Item label={ta("Date")}>
          <DatePicker
            style={{ width: "100%" }}
            format="DD.MM.YYYY"
            allowClear={false}
            value={date}
            onChange={setDate}
          />
        </Form.Item>
        <Form.Item label={ta("Reason")}>
          <Input value={reason} maxLength={200} onChange={e => setReason(e.target.value)} />
        </Form.Item>
        <Form.Item label={ta("Bank return fee")}>
          <MoneyInput min={0} value={returnFee} onChange={setReturnFee} />
        </Form.Item>
        <Checkbox
          checked={chargeFee}
          disabled={!returnFee}
          onChange={e => setChargeFee(e.target.checked)}
        >
          {ta("Charge the fee to the customer (with the next reminder)")}
        </Checkbox>
      </Form>
    </Modal>
  );
};

/** Payments of an invoice with the reverse action. */
export const PaymentsList = ({ payments, canReverse, onReverse }) => {
  const { t: ta } = useTranslation("accounting");
  const columns = [
    {
      title: ta("Date"),
      dataIndex: "date",
      mobile: "subtitle",
      render: d => formatDate(d)
    },
    {
      title: ta("Type"),
      key: "kind",
      mobile: "title",
      render: (_, p) => (
        <span>
          {ta(KIND_LABELS[p.kind] || p.kind)}
          {p.direction === "out" && p.kind !== "reversal" ? ` (${ta("paid out")})` : ""}
        </span>
      )
    },
    {
      title: ta("Method"),
      dataIndex: "method",
      render: m => ta(paymentMethodLabels[m] || m || "")
    },
    {
      title: ta("Amount"),
      key: "amount",
      align: "right",
      render: (_, p) => (
        <span className="invoice-payments__amount">
          {p.kind === "reversal" || p.direction === "out" ? "- " : ""}
          {formatCents(p.amount)}
        </span>
      )
    },
    {
      title: ta("Details"),
      key: "details",
      render: (_, p) => {
        const parts = [];
        if (p.skontoAmount) parts.push(`${ta("Skonto")} ${formatCents(p.skontoAmount)}`);
        if (p.smallDifferenceAmount)
          parts.push(`${ta("Small difference")} ${formatCents(p.smallDifferenceAmount)}`);
        if (p.withheldAmount)
          parts.push(`${ta("Construction withholding tax")} ${formatCents(p.withheldAmount)}`);
        if (p.note) parts.push(p.note);
        return (
          <Space size={4} wrap>
            {p.reversedBy ? <Tag color="error">{ta("Reversed")}</Tag> : null}
            {p.bankTransaction ? <Tag>{ta("Bank")}</Tag> : null}
            <Typography.Text type="secondary">{parts.join(" · ")}</Typography.Text>
          </Space>
        );
      }
    },
    {
      title: ta("Actions"),
      key: "actions",
      mobile: "actions",
      render: (_, p) =>
        canReverse &&
        p.kind === "payment" &&
        p.direction === "in" &&
        !p.reversedBy &&
        !p.bankTransaction ? (
          <Button size="small" danger onClick={() => onReverse(p)}>
            {ta("Reverse")}
          </Button>
        ) : null
    }
  ];
  return (
    <ResponsiveTable
      size="small"
      columns={columns}
      dataSource={payments}
      rowKey={p => String(p._id)}
      pagination={false}
      mobileAvatar={false}
    />
  );
};
